import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { isFreezeAvailable } from '../../shared/helpers/streakFreeze';
import { useStreaks } from '../../shared/api/apiHooks';
import Skeleton from 'react-loading-skeleton';
import './StreakModal.scss';
import { Modal } from 'semantic-ui-react';
import { Loader } from '../../components/Loader';
import { useSelector } from 'react-redux';
import { hasConsultantPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import analytics from '../../shared/services/analytics/analytics';
import { ErrorBoundary } from '../ErrorBoundary';
import { StreakWeekStrip } from './StreakWeekStrip';

interface StreakModalProps {
  parent: React.MutableRefObject<HTMLDivElement | null>;
}

export function getEncouragementTextId(active: number | undefined, isConsultant: boolean) {
  if (typeof active === 'undefined') {
    return 'Loading...';
  }
  const specialMessages = new Set([3, 5, 7, 10, 14, 30]);
  if (active === 0) {
    return 'streak.modal.congratulations.1';
  }

  if (specialMessages.has(active)) {
    if (isConsultant) {
      return `streak.modal.congratulations.consultant.${active}`;
    }
    return `streak.modal.congratulations.user.${active}`;
  }
  if (active <= 30) {
    return `streak.modal.congratulations.${active}`;
  }

  return `streak.modal.congratulations.long.${active % 10}`;
}

export function StreakModal({ parent }: StreakModalProps) {
  const { data: streaks, refetch } = useStreaks();
  const isSimulated = useSelector((state: any) => state.getIn(['editor', 'isSimulated']));
  const isConsultant = useSelector(hasConsultantPermission);
  const [isModalShown, setIsModalShown] = useState(false);
  // Guards against a "state update on an unmounted component" warning: the refetch below resolves
  // asynchronously and the editor can unmount (navigate away) before it settles.
  const isMountedRef = useRef(true);

  const handleModalClose = () => {
    setIsModalShown(false);
    analytics.trackCustomEvent('sim_click_close_streak_modal');
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isSimulated && !isModalShown && streaks && !streaks.activeToday) {
      refetch().then(() => {
        if (isMountedRef.current) {
          setIsModalShown(true);
        }
      });
    }
  }, [isSimulated, streaks]);

  if (parent == null || !parent.current || !streaks?.activeToday) {
    return <></>;
  }
  const encouragementTextId = getEncouragementTextId(streaks?.active, isConsultant);

  return (
    <ErrorBoundary showUI={true} isNotFullScreen={true} isModal={true}>
      <React.Fragment>
        <Modal
          className="streak-modal"
          mountNode={parent.current}
          closeIcon={true}
          open={isModalShown}
          onClose={handleModalClose}
          size="tiny"
        >
          <Modal.Content>
            {streaks ? (
              <>
                <StreakWeekStrip records={streaks.records} />
                <div className="streak-modal__current-streak">
                  {streaks ? streaks?.active : <Skeleton width="2em" />} <FormattedMessageHelper id="streak.modal.streakDays" />{' '}
                  <span className="streak-modal__wq-highlight">
                    {' '}
                    <FormattedMessageHelper noTag={true} id="streak.modal.wqBrain" />
                  </span>
                </div>

                {streaks.freeze?.eligible && (
                  <div className="streak-modal__freeze-availability">
                    <FormattedMessageHelper
                      id={isFreezeAvailable(streaks.freeze) ? 'streak.modal.freeze.available' : 'streak.modal.freeze.used'}
                    />
                  </div>
                )}

                <div className="streak-modal__encouragement-text">
                  <FormattedMessageHelper id={encouragementTextId} />
                </div>
                <div className="streak-modal__button-container">
                  <button className="button button--primary" onClick={handleModalClose}>
                    <FormattedMessageHelper id="streak.modal.close" />
                  </button>
                </div>
              </>
            ) : (
              <Loader modifiers={['small']} />
            )}
          </Modal.Content>
        </Modal>
      </React.Fragment>
    </ErrorBoundary>
  );
}
